import React from 'react';
import PropTypes from 'prop-types';
import { drapcodeIcon } from '../../../common/custom/icon-library';
import { getPlanStatusLabel, normalizePlanProposal } from '../utils/planUtils';

const PlanProposal = ({ proposal, isBusy, showActions, onApprove, onRequestChanges }) => {
  const normalized = normalizePlanProposal(proposal);
  if (!normalized || !normalized.plan) return null;
  const plan = normalized.plan;
  const tasks = Array.isArray(plan.tasks) ? plan.tasks : [];
  const routes = Array.isArray(plan.routes) ? plan.routes : [];
  const userFlows = Array.isArray(plan.userFlows) ? plan.userFlows : [];
  const design = plan.design && typeof plan.design === 'object' ? plan.design : null;
  const statusLabel = getPlanStatusLabel(normalized.status);

  return (
    <section className="vibe-plan" aria-label="Proposed implementation plan">
      <div className="vibe-plan__header">
        <div className="vibe-plan__title">
          <i className={`wb ${drapcodeIcon('list')}`} aria-hidden="true" />
          <strong>Implementation plan</strong>
        </div>
        <div className="vibe-plan__meta">
          {statusLabel && (
            <span className={`badge ${normalized.status === 'awaiting_approval' ? 'badge-info' : 'badge-success'}`}>
              {statusLabel}
            </span>
          )}
          <span className="badge badge-light">v{normalized.planVersion || 1}</span>
          <span className="badge badge-secondary">{tasks.length} tasks</span>
          <span className="badge badge-secondary">{routes.length} routes</span>
        </div>
      </div>

      {plan.audience && <p className="vibe-plan__audience">{plan.audience}</p>}

      {design && (
        <div className="vibe-plan__section">
          <div className="vibe-plan__section-title">Design direction</div>
          <dl className="vibe-plan__design">
            {design.presetId && (
              <>
                <dt>Theme</dt>
                <dd>{design.presetId}</dd>
              </>
            )}
            {design.layoutArchetype && (
              <>
                <dt>Layout</dt>
                <dd>{design.layoutArchetype}</dd>
              </>
            )}
            {design.heroStyle && (
              <>
                <dt>Hero</dt>
                <dd>{design.heroStyle}</dd>
              </>
            )}
            {design.colors && (
              <>
                <dt>Colors</dt>
                <dd>{design.colors}</dd>
              </>
            )}
            {design.typography && (
              <>
                <dt>Typography</dt>
                <dd>{design.typography}</dd>
              </>
            )}
          </dl>
        </div>
      )}

      {routes.length > 0 && (
        <div className="vibe-plan__section">
          <div className="vibe-plan__section-title">Pages</div>
          <ul className="vibe-plan__routes">
            {routes.slice(0, 6).map((route) => (
              <li key={route.path}>
                <code>{route.path}</code>
                <span>{route.title}</span>
              </li>
            ))}
            {routes.length > 6 && <li className="vibe-plan__more">+{routes.length - 6} more</li>}
          </ul>
        </div>
      )}

      <div className="vibe-plan__section">
        <div className="vibe-plan__section-title">Tasks</div>
        <ol className="vibe-plan__tasks">
          {tasks.map((task, idx) => (
            <li key={task.id || idx}>
              <span className="vibe-plan__task-id">{task.id || `task_${idx + 1}`}</span>
              <span className="vibe-plan__task-desc">{task.description}</span>
            </li>
          ))}
        </ol>
      </div>

      {userFlows.length > 0 && (
        <div className="vibe-plan__section">
          <div className="vibe-plan__section-title">User flows</div>
          <ul className="vibe-plan__flows">
            {userFlows.slice(0, 4).map((flow, idx) => (
              <li key={idx}>{flow}</li>
            ))}
          </ul>
        </div>
      )}

      {showActions && (
        <div className="vibe-plan__actions">
          <button
            type="button"
            className="btn btn-theme-submit btn-sm"
            onClick={onApprove}
            disabled={isBusy}
          >
            Approve &amp; Build
          </button>
          {onRequestChanges && (
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={onRequestChanges}
              disabled={isBusy}
            >
              Request changes
            </button>
          )}
        </div>
      )}
    </section>
  );
};

PlanProposal.propTypes = {
  proposal: PropTypes.object,
  isBusy: PropTypes.bool,
  showActions: PropTypes.bool,
  onApprove: PropTypes.func.isRequired,
  onRequestChanges: PropTypes.func,
};

PlanProposal.defaultProps = {
  proposal: null,
  isBusy: false,
  showActions: true,
  onRequestChanges: null,
};

export default PlanProposal;
